/**
 * ComparisonView — Version comparison panel on ContractDetail page.
 *
 * Provides version selectors, triggers AI-powered comparison, and displays
 * the change summary grouped by category with significance badges and
 * diff stats. Also shows past comparisons.
 *
 * RBAC: Only Admin and Contract Specialist can trigger comparisons.
 *
 * Requirements: 21.1, 21.2, 21.3, 21.4, 21.5, 21.6
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  comparisonService,
  type ComparisonResult,
  type ChangeSummary,
  type ChangeCategory,
  type CategoryChange,
} from '@/services/comparisonService';
import { contractService } from '@/services/contractService';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/types';
import type { DocumentVersion } from '@/types';

// ─── Constants ─────────────────────────────────────────────────────────

const CHANGE_TYPE_COLORS: Record<string, { bg: string; border: string; text: string; label: string }> = {
  addition: { bg: '#f0fdf4', border: '#86efac', text: '#166534', label: 'Addition' },
  removal: { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', label: 'Removal' },
  modification: { bg: '#eff6ff', border: '#93c5fd', text: '#1e40af', label: 'Modification' },
};

const SIGNIFICANCE_VARIANTS: Record<string, BadgeVariant> = {
  High: 'red',
  Medium: 'yellow',
  Low: 'green',
};

// ─── Props ─────────────────────────────────────────────────────────────

interface ComparisonViewProps {
  contractId: number;
  canWrite: boolean;
  egnyteFilePath: string | null;
}

// ─── Component ─────────────────────────────────────────────────────────

export function ComparisonView({ contractId, canWrite, egnyteFilePath }: ComparisonViewProps) {
  const queryClient = useQueryClient();
  const { hasAnyRole } = useAuth();

  const [version1, setVersion1] = useState('');
  const [version2, setVersion2] = useState('');
  const [activeResult, setActiveResult] = useState<ComparisonResult | null>(null);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [showPastComparisons, setShowPastComparisons] = useState(false);

  const canCompare = canWrite && hasAnyRole([UserRole.Admin, UserRole.ContractSpecialist]);

  // Fetch document versions for the selectors
  const { data: versionData } = useQuery({
    queryKey: ['contract-document-versions', contractId],
    queryFn: () => contractService.getDocumentVersions(contractId),
    enabled: !!egnyteFilePath,
  });

  // Fetch past comparisons
  const {
    data: pastComparisons,
    isLoading: pastLoading,
    error: pastError,
    refetch: refetchPast,
  } = useQuery({
    queryKey: ['contract-comparisons', contractId],
    queryFn: () => comparisonService.listComparisons(contractId),
    enabled: contractId > 0,
  });

  // Compare mutation
  const compareMutation = useMutation({
    mutationFn: () =>
      comparisonService.compareVersions(contractId, version1, version2),
    onSuccess: (result) => {
      setActiveResult(result);
      queryClient.invalidateQueries({ queryKey: ['contract-comparisons', contractId] });
    },
  });

  const versions: DocumentVersion[] = versionData?.versions ?? [];
  const hasVersions = versions.length >= 2;

  function toggleCategory(category: string) {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }

  function handleCompare() {
    if (!version1 || !version2 || version1 === version2) return;
    setActiveResult(null);
    compareMutation.mutate();
  }

  function getVersionLabel(entryId: string): string {
    const v = versions.find((ver) => ver.entryId === entryId);
    return v ? `v${v.versionNumber}` : entryId;
  }

  return (
    <div>
      {/* Header */}
      <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.75rem' }}>
        Version Comparison
      </h2>

      {/* No versions available */}
      {!hasVersions && (
        <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
          {versions.length < 2
            ? 'At least two document versions are needed to compare. Upload additional versions to Egnyte.'
            : 'No document versions available.'}
        </p>
      )}

      {/* Version Selectors + Compare Button */}
      {hasVersions && (
        <div style={{ marginBottom: '1rem' }}>
          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <label style={{ display: 'block', fontSize: '0.8rem' }}>
              <span style={{ color: '#475569', fontWeight: 500 }}>Version 1 (Original)</span>
              <select
                value={version1}
                onChange={(e) => setVersion1(e.target.value)}
                style={{
                  display: 'block',
                  marginTop: '0.25rem',
                  padding: '0.4rem 0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.8rem',
                  minWidth: '180px',
                }}
              >
                <option value="">Select version…</option>
                {versions.map((v) => (
                  <option key={v.entryId} value={v.entryId} disabled={v.entryId === version2}>
                    v{v.versionNumber}
                    {v.isCurrent ? ' (Current)' : ''}
                    {' — '}
                    {new Date(v.lastModified).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: 'block', fontSize: '0.8rem' }}>
              <span style={{ color: '#475569', fontWeight: 500 }}>Version 2 (Revised)</span>
              <select
                value={version2}
                onChange={(e) => setVersion2(e.target.value)}
                style={{
                  display: 'block',
                  marginTop: '0.25rem',
                  padding: '0.4rem 0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.8rem',
                  minWidth: '180px',
                }}
              >
                <option value="">Select version…</option>
                {versions.map((v) => (
                  <option key={v.entryId} value={v.entryId} disabled={v.entryId === version1}>
                    v{v.versionNumber}
                    {v.isCurrent ? ' (Current)' : ''}
                    {' — '}
                    {new Date(v.lastModified).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </label>

            {canCompare && (
              <button
                onClick={handleCompare}
                disabled={
                  !version1 ||
                  !version2 ||
                  version1 === version2 ||
                  compareMutation.isPending
                }
                style={{
                  padding: '0.4rem 0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #7c3aed',
                  backgroundColor:
                    !version1 || !version2 || version1 === version2 || compareMutation.isPending
                      ? '#ede9fe'
                      : '#7c3aed',
                  color:
                    !version1 || !version2 || version1 === version2 || compareMutation.isPending
                      ? '#7c3aed'
                      : '#fff',
                  cursor:
                    !version1 || !version2 || version1 === version2 || compareMutation.isPending
                      ? 'not-allowed'
                      : 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                {compareMutation.isPending ? 'Comparing…' : 'Compare Versions'}
              </button>
            )}
          </div>

          {version1 && version2 && version1 === version2 && (
            <p style={{ color: '#dc2626', fontSize: '0.75rem', margin: '0.35rem 0 0' }}>
              Please select two different versions to compare.
            </p>
          )}
        </div>
      )}

      {/* Processing State */}
      {compareMutation.isPending && (
        <div
          style={{
            marginBottom: '0.75rem',
            padding: '1rem',
            backgroundColor: '#f5f3ff',
            border: '1px solid #c4b5fd',
            borderRadius: '6px',
            textAlign: 'center',
          }}
        >
          <LoadingSpinner message="Comparing document versions… This may take 30–60 seconds." />
        </div>
      )}

      {/* Comparison Error */}
      {compareMutation.error && (
        <div style={{ marginBottom: '0.75rem' }}>
          <ErrorMessage
            message={(compareMutation.error as Error)?.message ?? 'Failed to compare versions.'}
            onRetry={handleCompare}
          />
        </div>
      )}

      {/* Active Comparison Result */}
      {activeResult && (
        <ComparisonResultDisplay
          result={activeResult}
          versions={versions}
          collapsedCategories={collapsedCategories}
          onToggleCategory={toggleCategory}
          getVersionLabel={getVersionLabel}
        />
      )}

      {/* Past Comparisons */}
      {hasVersions && (
        <div style={{ marginTop: '1rem' }}>
          <button
            onClick={() => setShowPastComparisons(!showPastComparisons)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#475569',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
            }}
            aria-expanded={showPastComparisons}
          >
            <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
              {showPastComparisons ? '▼' : '▶'}
            </span>
            Past Comparisons
            {pastComparisons && pastComparisons.length > 0 && (
              <span style={{ fontWeight: 400, color: '#64748b', fontSize: '0.8rem' }}>
                ({pastComparisons.length})
              </span>
            )}
          </button>

          {showPastComparisons && (
            <div style={{ marginTop: '0.5rem' }}>
              {pastLoading && <LoadingSpinner message="Loading past comparisons…" />}
              {pastError && (
                <ErrorMessage
                  message={(pastError as Error)?.message ?? 'Failed to load past comparisons.'}
                  onRetry={() => refetchPast()}
                />
              )}
              {!pastLoading && !pastError && pastComparisons && pastComparisons.length === 0 && (
                <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
                  No past comparisons for this contract.
                </p>
              )}
              {!pastLoading && pastComparisons && pastComparisons.length > 0 && (
                <PastComparisonsList
                  comparisons={pastComparisons}
                  getVersionLabel={getVersionLabel}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Comparison Result Display ─────────────────────────────────────────

interface ComparisonResultDisplayProps {
  result: ComparisonResult;
  versions: DocumentVersion[];
  collapsedCategories: Set<string>;
  onToggleCategory: (category: string) => void;
  getVersionLabel: (entryId: string) => string;
}

function ComparisonResultDisplay({
  result,
  collapsedCategories,
  onToggleCategory,
  getVersionLabel,
}: ComparisonResultDisplayProps) {
  const { changeSummary, disclaimer } = result;

  return (
    <div>
      {/* AI Disclaimer */}
      <div
        role="alert"
        style={{
          marginBottom: '0.75rem',
          padding: '0.6rem 0.75rem',
          backgroundColor: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.5rem',
          fontSize: '0.8rem',
        }}
      >
        <span style={{ fontSize: '1rem', lineHeight: 1 }} aria-hidden="true">⚠️</span>
        <span style={{ color: '#92400e', fontWeight: 500 }}>{disclaimer}</span>
      </div>

      {/* Diff Stats */}
      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          marginBottom: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <DiffStatBadge
          label="Additions"
          count={changeSummary.totalAdditions}
          color="#166534"
          bg="#f0fdf4"
          border="#86efac"
        />
        <DiffStatBadge
          label="Removals"
          count={changeSummary.totalRemovals}
          color="#991b1b"
          bg="#fef2f2"
          border="#fca5a5"
        />
        <DiffStatBadge
          label="Modifications"
          count={changeSummary.totalModifications}
          color="#1e40af"
          bg="#eff6ff"
          border="#93c5fd"
        />
      </div>

      {/* Compared versions label */}
      <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '0 0 0.75rem' }}>
        Comparing {getVersionLabel(changeSummary.version1EntryId)} →{' '}
        {getVersionLabel(changeSummary.version2EntryId)}
        {' · '}
        {new Date(changeSummary.createdAt).toLocaleDateString()} by {changeSummary.createdBy}
      </p>

      {/* Change Categories */}
      {changeSummary.categories.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
          No changes detected between the selected versions.
        </p>
      ) : (
        changeSummary.categories.map((cat) => (
          <ChangeCategoryGroup
            key={cat.category}
            category={cat}
            isCollapsed={collapsedCategories.has(cat.category)}
            onToggle={() => onToggleCategory(cat.category)}
          />
        ))
      )}
    </div>
  );
}

// ─── Diff Stat Badge ───────────────────────────────────────────────────

interface DiffStatBadgeProps {
  label: string;
  count: number;
  color: string;
  bg: string;
  border: string;
}

function DiffStatBadge({ label, count, color, bg, border }: DiffStatBadgeProps) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
        padding: '0.3rem 0.6rem',
        borderRadius: '6px',
        border: `1px solid ${border}`,
        backgroundColor: bg,
        fontSize: '0.8rem',
        fontWeight: 600,
        color,
      }}
    >
      <span>{count}</span>
      <span style={{ fontWeight: 400 }}>{label}</span>
    </div>
  );
}

// ─── Change Category Group ─────────────────────────────────────────────

interface ChangeCategoryGroupProps {
  category: ChangeCategory;
  isCollapsed: boolean;
  onToggle: () => void;
}

function ChangeCategoryGroup({ category, isCollapsed, onToggle }: ChangeCategoryGroupProps) {
  const addCount = category.changes.filter((c) => c.type === 'addition').length;
  const remCount = category.changes.filter((c) => c.type === 'removal').length;
  const modCount = category.changes.filter((c) => c.type === 'modification').length;

  return (
    <div
      style={{
        marginBottom: '0.75rem',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        overflow: 'hidden',
      }}
    >
      {/* Category Header */}
      <button
        onClick={onToggle}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          padding: '0.6rem 0.75rem',
          backgroundColor: '#f8fafc',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: '0.85rem',
          fontWeight: 600,
          color: '#1e293b',
        }}
        aria-expanded={!isCollapsed}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
            {isCollapsed ? '▶' : '▼'}
          </span>
          {category.category}
          <span style={{ fontWeight: 400, color: '#64748b', fontSize: '0.8rem' }}>
            ({category.changes.length} change{category.changes.length !== 1 ? 's' : ''})
          </span>
        </span>
        <span style={{ display: 'flex', gap: '0.35rem' }}>
          {addCount > 0 && <StatusBadge label={`+${addCount}`} variant="green" />}
          {remCount > 0 && <StatusBadge label={`-${remCount}`} variant="red" />}
          {modCount > 0 && <StatusBadge label={`~${modCount}`} variant="blue" />}
        </span>
      </button>

      {/* Change Cards */}
      {!isCollapsed && (
        <div style={{ padding: '0.5rem 0.75rem' }}>
          {category.changes.map((change, idx) => (
            <ChangeCard key={`${category.category}-${idx}`} change={change} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Change Card ───────────────────────────────────────────────────────

interface ChangeCardProps {
  change: CategoryChange;
}

function ChangeCard({ change }: ChangeCardProps) {
  const typeStyle = CHANGE_TYPE_COLORS[change.type] ?? CHANGE_TYPE_COLORS.modification;

  return (
    <div
      style={{
        marginBottom: '0.5rem',
        padding: '0.75rem',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        backgroundColor: '#fff',
      }}
    >
      {/* Header: type badge + significance badge */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.4rem',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            padding: '0.15rem 0.5rem',
            borderRadius: '4px',
            border: `1px solid ${typeStyle.border}`,
            backgroundColor: typeStyle.bg,
            color: typeStyle.text,
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'capitalize',
          }}
        >
          {typeStyle.label}
        </span>
        <StatusBadge
          label={change.significance}
          variant={SIGNIFICANCE_VARIANTS[change.significance] ?? 'gray'}
        />
      </div>

      {/* Description */}
      <p style={{ margin: '0 0 0.4rem', fontSize: '0.8rem', color: '#334155' }}>
        {change.description}
      </p>

      {/* Original Text */}
      {change.originalText && (
        <div style={{ marginBottom: '0.3rem' }}>
          <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500 }}>
            Original:
          </span>
          <p
            style={{
              margin: '0.15rem 0 0',
              fontSize: '0.78rem',
              color: '#334155',
              backgroundColor: '#fef2f2',
              padding: '0.35rem 0.5rem',
              borderRadius: '4px',
              borderLeft: '3px solid #fca5a5',
            }}
          >
            {change.originalText}
          </p>
        </div>
      )}

      {/* Revised Text */}
      {change.revisedText && (
        <div>
          <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500 }}>
            Revised:
          </span>
          <p
            style={{
              margin: '0.15rem 0 0',
              fontSize: '0.78rem',
              color: '#334155',
              backgroundColor: '#f0fdf4',
              padding: '0.35rem 0.5rem',
              borderRadius: '4px',
              borderLeft: '3px solid #86efac',
            }}
          >
            {change.revisedText}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Past Comparisons List ─────────────────────────────────────────────

interface PastComparisonsListProps {
  comparisons: ChangeSummary[];
  getVersionLabel: (entryId: string) => string;
}

function PastComparisonsList({ comparisons, getVersionLabel }: PastComparisonsListProps) {
  return (
    <table
      style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}
      role="table"
      aria-label="Past version comparisons"
    >
      <thead>
        <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
          <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>Date</th>
          <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>Compared By</th>
          <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>Versions</th>
          <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: '#475569' }}>Changes</th>
        </tr>
      </thead>
      <tbody>
        {comparisons.map((comp) => (
          <tr key={comp.summaryId} style={{ borderBottom: '1px solid #e2e8f0' }}>
            <td style={{ padding: '0.4rem 0.5rem' }}>
              {new Date(comp.createdAt).toLocaleDateString()}
            </td>
            <td style={{ padding: '0.4rem 0.5rem' }}>{comp.createdBy}</td>
            <td style={{ padding: '0.4rem 0.5rem' }}>
              {getVersionLabel(comp.version1EntryId)} → {getVersionLabel(comp.version2EntryId)}
            </td>
            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>
              <span style={{ display: 'inline-flex', gap: '0.35rem' }}>
                {comp.totalAdditions > 0 && (
                  <span style={{ color: '#166534', fontWeight: 500 }}>+{comp.totalAdditions}</span>
                )}
                {comp.totalRemovals > 0 && (
                  <span style={{ color: '#991b1b', fontWeight: 500 }}>-{comp.totalRemovals}</span>
                )}
                {comp.totalModifications > 0 && (
                  <span style={{ color: '#1e40af', fontWeight: 500 }}>~{comp.totalModifications}</span>
                )}
                {comp.totalAdditions === 0 && comp.totalRemovals === 0 && comp.totalModifications === 0 && (
                  <span style={{ color: '#94a3b8' }}>No changes</span>
                )}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
